import { useSyncExternalStore } from 'react'
import { entityImages, type ImageEntry } from '../data/guide/entityImages'
import { monsterPhotos, monsterSheets, MONSTER_SHEET_SIZE, type MonsterPhoto } from '../data/guide/monsterPhotoSheets'
import { useWorld } from '../contexts/WorldDataContext'
import type { ClassId } from '../types/progression'
import type { WorldData } from '../types/world'

export type ResolvedImageEntry = ImageEntry & {
  resolvedMonsterIds: number[]
  ambiguous: boolean
  notInCatalog: boolean
}

const byClass = new Map(entityImages.filter((e) => e.classId).map((e) => [e.classId!, e]))
export const imageEntryForClass = (id: ClassId) => byClass.get(id)

export type PackStatus = 'nueva' | 'conservada' | 'fuera de catálogo'
export type PackRow = { photo: MonsterPhoto; status: PackStatus; catalogName?: string }

const PACK_DATE = '11/10/2026'

/** Entrada del paquete VANGUARD_MOBS_LIMPIOS para un ID exacto (recorte de su lámina). */
function packEntry(p: MonsterPhoto, label: string): ImageEntry {
  return {
    key: `paquete:${p.id}`,
    label,
    kind: 'monstruo',
    monsterId: p.id,
    image: {
      src: monsterSheets[p.sheet],
      origin: 'paquete',
      addedOn: PACK_DATE,
      matchedBy: `ID ${p.id} impreso bajo la foto en VANGUARD_MOBS_LIMPIOS_${String(p.sheet).padStart(2, '0')} · índice JSON no recibido (procedencia y retoque pendientes)`,
      sourceName: p.sourceName,
      crop: { sheetW: MONSTER_SHEET_SIZE, sheetH: MONSTER_SHEET_SIZE, rect: p.rect, focus: p.focus },
    },
  }
}

const cache = new WeakMap<WorldData, { byMonster: Map<number, ImageEntry>; resolved: ResolvedImageEntry[]; pack: PackRow[] }>()

/** Une el registro con 06_monsters. Por nombre solo se asigna con una única coincidencia exacta. */
export function resolveImageEntries(w: WorldData) {
  const hit = cache.get(w)
  if (hit) return hit
  const nameIndex = new Map<string, number[]>()
  w.monsters.forEach((m) => {
    const k = m.name.trim().toLowerCase()
    nameIndex.set(k, [...(nameIndex.get(k) ?? []), m.id])
  })
  const byMonster = new Map<number, ImageEntry>()
  const resolved: ResolvedImageEntry[] = entityImages.map((e) => {
    if (e.kind === 'clase') return { ...e, resolvedMonsterIds: [], ambiguous: false, notInCatalog: false }
    if (e.monsterId !== undefined) {
      const exists = w.monsters.has(e.monsterId)
      if (exists) byMonster.set(e.monsterId, e)
      return { ...e, resolvedMonsterIds: exists ? [e.monsterId] : [], ambiguous: false, notInCatalog: !exists }
    }
    const ids = nameIndex.get((e.monsterName ?? '').trim().toLowerCase()) ?? []
    if (ids.length === 1) byMonster.set(ids[0], e)
    return { ...e, resolvedMonsterIds: ids, ambiguous: ids.length > 1, notInCatalog: ids.length === 0 }
  })
  // Paquete: solo completa IDs sin foto del staff (incluidos los que el staff reclama por nombre, aunque sea ambiguo).
  const claimed = new Set(resolved.flatMap((e) => e.resolvedMonsterIds))
  const pack: PackRow[] = monsterPhotos.map((p) => {
    const m = w.monsters.get(p.id)
    if (!m) return { photo: p, status: 'fuera de catálogo' }
    if (claimed.has(p.id)) return { photo: p, status: 'conservada', catalogName: m.name }
    byMonster.set(p.id, packEntry(p, m.name))
    return { photo: p, status: 'nueva', catalogName: m.name }
  })
  const out = { byMonster, resolved, pack }
  cache.set(w, out)
  return out
}

const packById = new Map(monsterPhotos.map((p) => [p.id, p]))

/** Entrada por ID de monstruo/NPC. Sin catálogo cargado: staff con ID fijo y, si no hay, el paquete. */
export const imageEntryForMonster = (w: WorldData | undefined, monsterId: number): ImageEntry | undefined => {
  if (w) return resolveImageEntries(w).byMonster.get(monsterId)
  const staffEntry = entityImages.find((e) => e.monsterId === monsterId)
  if (staffEntry) return staffEntry
  const p = packById.get(monsterId)
  return p ? packEntry(p, p.sourceName) : undefined
}

/** Misma asociación por ID en cualquier componente. */
export function useMonsterImage(monsterId: number | undefined): ImageEntry | undefined {
  const { data } = useWorld()
  return monsterId === undefined ? undefined : imageEntryForMonster(data, monsterId)
}

/* Registro de imágenes que no cargan, para el panel del staff. */
const failed = new Set<string>()
const listeners = new Set<() => void>()
let snapshot: string[] = []

export function markImageFailed(src: string) {
  if (failed.has(src)) return
  failed.add(src)
  snapshot = Array.from(failed)
  listeners.forEach((l) => l())
}

export function useFailedImages() {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => snapshot,
  )
}
